// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/contador/ArquivosFiscaisBotao.tsx
//  Gera o SPED Fiscal (EFD ICMS/IPI) ou o Sintegra do mês a partir dos XMLs do cofre.
//  O arquivo baixa na hora; o diálogo mostra o que ficou de fora para o contador
//  completar no programa dele (inventário, E116, CT-e, CPF do contador…).
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, FileSpreadsheet, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { contadorService, rotuloCompetencia, type ArquivoFiscal } from './contadorService';

type Tipo = 'sped-fiscal' | 'sintegra';

const TITULO: Record<Tipo, string> = { 'sped-fiscal': 'SPED Fiscal (EFD ICMS/IPI)', sintegra: 'Sintegra' };

/** Os 2 primeiros dígitos do código IBGE do município são o da UF. */
const UF_POR_CODIGO: Record<string, string> = {
  12: 'AC', 27: 'AL', 13: 'AM', 16: 'AP', 29: 'BA', 23: 'CE', 53: 'DF', 32: 'ES', 52: 'GO', 21: 'MA', 31: 'MG', 50: 'MS', 51: 'MT',
  15: 'PA', 25: 'PB', 26: 'PE', 22: 'PI', 41: 'PR', 33: 'RJ', 24: 'RN', 11: 'RO', 14: 'RR', 43: 'RS', 42: 'SC', 28: 'SE', 35: 'SP', 17: 'TO',
};

const UFS = ['AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS', 'MT', 'PA', 'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO'];

/** Contabilista do registro 0100: CPF, CRC e município (código IBGE pelo nome). Fica guardado no usuário. */
interface Contabilista { cpf: string; crc: string; uf: string; municipio: string; codMunicipio: string }

export function ArquivosFiscaisBotao({ contribuinteId, competencia, tipo }: { contribuinteId: number; competencia: number; tipo: Tipo }) {
  const [aberto, setAberto] = useState(false);
  const [perfil, setPerfil] = useState('A');
  const [finalidade, setFinalidade] = useState('1');
  const [gerando, setGerando] = useState(false);
  const [resultado, setResultado] = useState<ArquivoFiscal | null>(null);
  const [contador, setContador] = useState<Contabilista | null>(null);
  const sped = tipo === 'sped-fiscal';

  const { data: guardado } = useQuery({
    queryKey: ['contador', 'dados-contador'],
    queryFn: () => contadorService.dadosContador(),
    enabled: aberto && sped,
  });
  // Primeira abertura: começa com o que está guardado no usuário.
  const c: Contabilista = contador ?? {
    cpf: guardado?.cpf ?? '', crc: guardado?.crc ?? '', uf: (guardado?.codMunicipio && UF_POR_CODIGO[guardado.codMunicipio.slice(0, 2)]) || 'MG',
    municipio: guardado?.municipio ?? '', codMunicipio: guardado?.codMunicipio ?? '',
  };
  const mudar = (p: Partial<Contabilista>) => setContador({ ...c, ...p });
  const { data: municipios } = useQuery({
    queryKey: ['contador', 'municipios', c.uf, c.municipio],
    queryFn: () => contadorService.municipios(c.uf, c.municipio),
    enabled: aberto && sped && c.municipio.trim().length >= 2 && !c.codMunicipio,
  });

  const gerar = async () => {
    setGerando(true);
    try {
      if (sped && contador) {
        const cod = contador.codMunicipio || municipios?.find((m) => m.nome.toLowerCase() === contador.municipio.trim().toLowerCase())?.codigo || '';
        if (contador.municipio.trim() && !cod) throw new Error('Escolha o município na lista.');
        await contadorService.salvarDadosContador({ cpf: contador.cpf, crc: contador.crc, codMunicipio: cod, municipio: contador.municipio.trim() });
      }
      setResultado(await contadorService.arquivoFiscal(contribuinteId, tipo, competencia,
        tipo === 'sped-fiscal' ? { perfil } : { finalidade: Number(finalidade) }));
    } catch (e) {
      const api = (e as { response?: { data?: { mensagem?: string } } }).response?.data?.mensagem;
      toast.error(api ?? (e as Error).message);
    } finally {
      setGerando(false);
    }
  };

  return (
    <Dialog open={aberto} onOpenChange={(v) => { setAberto(v); if (!v) setResultado(null); }}>
      <DialogTrigger asChild>
        <Button variant="outline"><FileSpreadsheet className="mr-2 h-4 w-4" />{tipo === 'sped-fiscal' ? 'SPED Fiscal' : 'Sintegra'}</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{TITULO[tipo]} de {rotuloCompetencia(competencia).toLowerCase()}</DialogTitle>
          <DialogDescription>
            Montado com as NF-e e NFC-e do cofre. Serve de base: o que os XMLs não trazem aparece na lista de avisos para completar no seu programa.
          </DialogDescription>
        </DialogHeader>

        {!resultado ? (
          <div className="space-y-1.5">
            {tipo === 'sped-fiscal' ? (
              <>
                <Label>Perfil de apresentação</Label>
                <Select value={perfil} onValueChange={setPerfil}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A">Perfil A</SelectItem>
                    <SelectItem value="B">Perfil B</SelectItem>
                    <SelectItem value="C">Perfil C</SelectItem>
                  </SelectContent>
                </Select>
                <p className="pt-3 text-sm font-medium">Contabilista (registro 0100)</p>
                <p className="text-xs text-muted-foreground">O PVA exige CPF, CRC e município. Ficam guardados para as próximas vezes.</p>
                <div className="grid gap-3 sm:grid-cols-2 pt-1">
                  <div className="space-y-1.5">
                    <Label htmlFor="ct-cpf">CPF</Label>
                    <Input id="ct-cpf" inputMode="numeric" value={c.cpf} onChange={(e) => mudar({ cpf: e.target.value })} placeholder="000.000.000-00" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="ct-crc">CRC</Label>
                    <Input id="ct-crc" value={c.crc} onChange={(e) => mudar({ crc: e.target.value })} placeholder="MG-123456/O-7" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>UF</Label>
                    <Select value={c.uf} onValueChange={(uf) => mudar({ uf, municipio: '', codMunicipio: '' })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{UFS.map((uf) => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="ct-mun">Município</Label>
                    <Input id="ct-mun" list="ct-municipios" value={c.municipio} placeholder="Digite e escolha na lista"
                      onChange={(e) => {
                        const escolhido = municipios?.find((m) => m.nome === e.target.value);
                        mudar({ municipio: e.target.value, codMunicipio: escolhido?.codigo ?? '' });
                      }} />
                    <datalist id="ct-municipios">{municipios?.map((m) => <option key={m.codigo} value={m.nome} />)}</datalist>
                    {c.codMunicipio && <p className="text-xs text-muted-foreground">Código IBGE {c.codMunicipio}</p>}
                  </div>
                </div>
              </>
            ) : (
              <>
                <Label>Finalidade</Label>
                <Select value={finalidade} onValueChange={setFinalidade}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Normal</SelectItem>
                    <SelectItem value="2">Retificação total</SelectItem>
                    <SelectItem value="3">Retificação aditiva</SelectItem>
                    <SelectItem value="5">Desfazimento</SelectItem>
                  </SelectContent>
                </Select>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-2 text-sm">
            <p>
              <span className="font-medium">{resultado.nome}</span> baixado: {resultado.documentos} documento(s), {resultado.linhas} linha(s).
            </p>
            {resultado.avisos.length > 0 && (
              <div className="rounded-md border border-amber-500 p-3 space-y-1">
                <p className="flex items-center gap-2 font-medium"><AlertTriangle className="h-4 w-4 text-amber-600" />Complete antes de transmitir</p>
                <ul className="list-disc pl-5 space-y-0.5 text-muted-foreground">
                  {resultado.avisos.map((a) => <li key={a}>{a}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {resultado ? (
            <Button variant="outline" onClick={() => setResultado(null)}>Gerar de novo</Button>
          ) : (
            <Button onClick={gerar} disabled={gerando}>
              {gerando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-2 h-4 w-4" />}Gerar e baixar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
